// LoginPage.js
import React, { useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useMediaQuery, useTheme } from "@mui/material";
import appImage from "./image.png";
import logo from "./kathys-logo.webp";
import { auth } from "../../firebase";
import {
  RemoveRedEye,
  RemoveRedEyeOutlined,
  VisibilityOff,
  VisibilityOffOutlined,
} from "@mui/icons-material";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import "./LoginPage.css"; // We'll need to add responsive styles here

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Redirect or handle successful login here
      console.log("Login successful");
    } catch (error) {
      setError(error.message);
      console.error("Login error:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      alert("Password reset email sent! Check your inbox.");
    } catch (error) {
      setError(error.message);
      console.error("Password reset error:", error.message);
    }
  };

  const handleSocialLogin = (provider) => {
    // This would implement social login in a real app
    alert(`${provider} login not implemented in this demo`);
  };

  // Responsive styles
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          flexDirection: "column",
          minHeight: "100vh",
        },
        logoContainer: {
          position: "static",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px 0",
          background: "rgba(255, 255, 255, 0.1)",
        },
        logo: {
          height: "50px",
        },
        formContainer: {
          width: "100%",
          padding: "30px 20px",
          background: "white",
          borderRadius: "0",
          boxShadow: "none",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        },
        heroSection: {
          display: "none", // Hide hero image on mobile
        },
        title: {
          fontSize: "24px",
          marginBottom: "8px",
          textAlign: "center",
        },
        subtitle: {
          fontSize: "16px",
          textAlign: "center",
          marginBottom: "30px",
        },
        inputGroup: {
          marginBottom: "20px",
        },
        input: {
          padding: "14px 16px",
          fontSize: "16px",
          borderRadius: "8px",
        },
        button: {
          padding: "14px",
          fontSize: "16px",
          marginTop: "10px",
        },
      };
    } else if (isTablet) {
      return {
        container: {
          flexDirection: "row",
          minHeight: "100vh",
        },
        logoContainer: {
          position: "absolute",
          top: "20px",
          left: "20px",
          zIndex: 10,
        },
        logo: {
          height: "50px",
        },
        formContainer: {
          width: "60%",
          padding: "40px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        },
        heroSection: {
          width: "40%",
          background: "#f5f6f7",
          position: "relative",
          overflow: "hidden",
        },
        heroImage: {
          height: "300px",
          right: "-50px",
          bottom: "50px",
        },
        title: {
          fontSize: "28px",
          marginBottom: "8px",
        },
        subtitle: {
          fontSize: "16px",
          marginBottom: "30px",
        },
        inputGroup: {
          marginBottom: "20px",
        },
        input: {
          padding: "12px 16px",
          fontSize: "15px",
        },
        button: {
          padding: "12px",
          fontSize: "15px",
        },
      };
    } else {
      // Desktop styles (original)
      return {
        container: {
          flexDirection: "row",
          minHeight: "100vh",
        },
        logoContainer: {
          position: "absolute",
          top: "20px",
          left: "30px",
          zIndex: 10,
        },
        logo: {
          height: "60px",
        },
        formContainer: {
          width: "50%",
          padding: "50px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        },
        heroSection: {
          width: "50%",
          background: "#f5f6f7",
          position: "relative",
          overflow: "hidden",
        },
        heroImage: {
          height: "500px",
          right: "-100px",
          bottom: "0",
        },
        title: {
          fontSize: "32px",
          marginBottom: "8px",
        },
        subtitle: {
          fontSize: "16px",
          marginBottom: "40px",
        },
        inputGroup: {
          marginBottom: "24px",
        },
        input: {
          padding: "14px 16px",
          fontSize: "16px",
        },
        button: {
          padding: "14px",
          fontSize: "16px",
        },
      };
    }
  };

  const styles = getResponsiveStyles();

  return (
    <div
      className="login-container"
      style={{
        display: "flex",
        ...styles.container,
      }}
    >
      {/* Logo */}
      <div style={styles.logoContainer}>
        <img src={logo} alt="Company Logo" style={styles.logo} />
      </div>

      {/* Login Form */}
      <div className="login-form-container" style={styles.formContainer}>
        <h1
          style={{
            fontWeight: "500",
            ...styles.title,
            color: isMobile ? "#333" : "inherit",
          }}
        >
          Sign In
        </h1>
        <p
          className="login-subtitle"
          style={{
            color: "#666",
            ...styles.subtitle,
          }}
        >
          Fill the fields below to continue
        </p>

        {error && (
          <div
            className="error-message"
            style={{
              background: "#fee",
              color: "#c33",
              padding: "12px",
              borderRadius: "6px",
              marginBottom: "20px",
              fontSize: isMobile ? "14px" : "16px",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div className="input-group" style={styles.inputGroup}>
            <label
              htmlFor="email"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#333",
                fontSize: isMobile ? "14px" : "16px",
              }}
            >
              Email Address
            </label>
            <div className="input-wrapper">
              <input
                id="email"
                type="email"
                placeholder="test@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: "100%",

                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.3s ease",
                  ...styles.input,
                }}
                onFocus={(e) => (e.target.style.borderColor = "#667eea")}
                onBlur={(e) => (e.target.style.borderColor = "#e1e5e9")}
              />
            </div>
          </div>

          <div className="input-group" style={styles.inputGroup}>
            <label
              htmlFor="password"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#333",
                fontSize: isMobile ? "14px" : "16px",
              }}
            >
              Password
            </label>
            <div
              className="input-wrapper"
              style={{
                position: "relative",
              }}
            >
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: "100%",

                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.3s ease",
                  paddingRight: "50px",
                  ...styles.input,
                }}
                onFocus={(e) => (e.target.style.borderColor = "#667eea")}
                onBlur={(e) => (e.target.style.borderColor = "#e1e5e9")}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#666",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {showPassword ? (
                  <RemoveRedEyeOutlined
                    fontSize={isMobile ? "small" : "medium"}
                  />
                ) : (
                  <VisibilityOffOutlined
                    fontSize={isMobile ? "small" : "medium"}
                  />
                )}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div style={{ textAlign: "right", marginBottom: "20px" }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{
                background: "none",
                border: "none",
                color: "#667eea",
                cursor: "pointer",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
              }}
            >
              Forgot Password?
            </button>
          </div>

          <CustomButton type="submit" className="login-btn" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </CustomButton>
        </form>
      </div>

      {/* Hero Image Section - Hidden on Mobile */}
      {!isMobile && (
        <div className="hero-image" style={styles.heroSection}>
          <div
            className="hero-content"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={appImage}
              alt="App Preview"
              style={{
                borderRadius: "20px 0px 20px 0px",
                position: "absolute",
                boxShadow: "rgba(100, 100, 111, 0.5) 0px 7px 29px 0px",
                objectFit: "cover",
                ...styles.heroImage,
              }}
            />

            {/* Decorative Elements for Tablet/Desktop */}
            {!isMobile && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: "10%",
                    left: "10%",
                    width: "100px",
                    height: "100px",

                    borderRadius: "50%",
                    opacity: "0.1",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    width: "60px",
                    height: "60px",
                    borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
                    opacity: "0.15",
                  }}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
